const FAILURE_HINTS = {
  no_email: {
    summary: (n) =>
      n === 1
        ? "1 cliente selecionado não tem e-mail cadastrado."
        : `${n} clientes selecionados não têm e-mail cadastrado.`,
    action: "Abra o cadastro do cliente, informe um e-mail válido e tente enviar o convite novamente.",
  },
  no_channel: {
    summary: () => "O Portal do Cliente ainda não está configurado para sua empresa.",
    action: "Em Configurações → Canais, crie um canal do tipo Portal do cliente e tente novamente.",
  },
  no_project: {
    summary: (n) =>
      n === 1
        ? "1 cliente não está vinculado a uma empresa do sistema."
        : `${n} clientes não estão vinculados a uma empresa do sistema.`,
    action: "Revise o cadastro do cliente ou fale com o suporte interno para corrigir o vínculo.",
  },
  forbidden: {
    summary: (n) =>
      n === 1
        ? "Você não tem permissão para convidar 1 dos clientes selecionados."
        : `Você não tem permissão para convidar ${n} dos clientes selecionados.`,
    action: "Peça acesso ao administrador do workspace ou selecione apenas clientes da sua empresa.",
  },
  not_found: {
    summary: (n) =>
      n === 1
        ? "1 cadastro selecionado não foi encontrado."
        : `${n} cadastros selecionados não foram encontrados.`,
    action: "Atualize a lista e tente novamente.",
  },
  mail_error: {
    summary: (n) =>
      n === 1
        ? "Não foi possível enviar o e-mail de convite para 1 cliente."
        : `Não foi possível enviar o e-mail de convite para ${n} clientes.`,
    action: "Confira se o e-mail está correto e tente de novo em instantes. Se o problema continuar, fale com o suporte.",
  },
  access_required: {
    summary: () => "Alguns clientes ainda não têm acesso liberado ao portal.",
    action: "Use esta mesma ação em massa para liberar o acesso e enviar o convite.",
  },
};

const API_MESSAGE_MAP = {
  "Selecione ao menos um cliente.":
    "Nenhum cliente foi selecionado. Marque pelo menos um cliente na lista e tente novamente.",
  "Envie no máximo 100 convites por vez.":
    "É possível enviar no máximo 100 convites por vez. Selecione menos clientes e repita a operação.",
  "Crie um canal do tipo Portal do cliente em Configurações → Canais antes de enviar convites.":
    "O Portal do Cliente ainda não está configurado. Crie o canal em Configurações → Canais e tente novamente.",
  "Erro ao processar convites do portal.":
    "Não foi possível concluir o envio dos convites agora. Aguarde um momento e tente novamente.",
};

function groupFailures(results = []) {
  const groups = {};
  for (const row of results) {
    if (row?.ok) continue;
    const code = row?.code || "unknown";
    groups[code] = (groups[code] || 0) + 1;
  }
  return groups;
}

function buildFailureLines(groups, t) {
  return Object.entries(groups).flatMap(([code, count]) => {
    const hint = FAILURE_HINTS[code];
    if (!hint) {
      return [
        count === 1
          ? t("clientTable.portalInviteUnknownFailureOne")
          : t("clientTable.portalInviteUnknownFailureMany", { count }),
      ];
    }
    return [hint.summary(count), hint.action];
  });
}

/**
 * Monta feedback amigável após POST /clients/portal/invite-bulk.
 * @returns {{ severity: 'success'|'warning'|'error', title: string, lines: string[] }}
 */
export function buildPortalBulkInviteFeedback(summary, t) {
  const sent = Number(summary?.sent) || 0;
  const total = Number(summary?.total) || 0;
  const failed = Number(summary?.failed) || Math.max(0, total - sent);
  const results = Array.isArray(summary?.results) ? summary.results : [];
  const accessGranted = results.filter((r) => r.ok && r.accessGranted).length;

  if (sent > 0 && failed === 0) {
    const lines = [
      sent === 1
        ? t("clientTable.portalInviteSuccessOne")
        : t("clientTable.portalInviteSuccessMany", { count: sent }),
      t("clientTable.portalInviteSuccessHint"),
    ];
    if (accessGranted > 0) {
      lines.splice(
        1,
        0,
        accessGranted === 1
          ? t("clientTable.portalInviteAccessGrantedOne")
          : t("clientTable.portalInviteAccessGrantedMany", { count: accessGranted })
      );
    }
    return {
      severity: "success",
      title: t("clientTable.portalInviteSuccessTitle"),
      lines,
    };
  }

  const failureGroups = groupFailures(results);
  const failureLines = buildFailureLines(failureGroups, t);

  if (sent > 0 && failed > 0) {
    return {
      severity: "warning",
      title: t("clientTable.portalInvitePartialTitle"),
      lines: [
        sent === 1
          ? t("clientTable.portalInvitePartialSentOne", { total })
          : t("clientTable.portalInvitePartialSentMany", { sent, total }),
        ...failureLines,
      ],
    };
  }

  return {
    severity: "error",
    title: t("clientTable.portalInviteErrorTitle"),
    lines:
      failureLines.length > 0
        ? failureLines
        : [t("clientTable.portalInviteErrorHint")],
  };
}

export function mapPortalBulkInviteApiError(err, t) {
  const raw = String(err?.message || "").trim();
  if (raw && API_MESSAGE_MAP[raw]) {
    return {
      severity: "error",
      title: t("clientTable.portalInviteErrorTitle"),
      lines: [API_MESSAGE_MAP[raw], err?.userFacingAction].filter(Boolean),
    };
  }

  if (/network|failed to fetch|err_network/i.test(raw) || !err?.response) {
    return {
      severity: "error",
      title: t("clientTable.portalInviteErrorTitle"),
      lines: [t("clientTable.portalInviteNetworkError")],
    };
  }

  if (raw && !/^(500|401|403|404|400)$/i.test(raw)) {
    return {
      severity: "error",
      title: t("clientTable.portalInviteErrorTitle"),
      lines: [raw, err?.userFacingAction].filter(Boolean),
    };
  }

  return {
    severity: "error",
    title: t("clientTable.portalInviteErrorTitle"),
    lines: [t("clientTable.portalInviteErrorHint")],
  };
}

export function summarizePortalInviteSelection(clients = []) {
  const list = Array.isArray(clients) ? clients : [];
  const withEmail = list.filter((c) => String(c?.email || "").trim());
  const withoutEmail = list.filter((c) => !String(c?.email || "").trim());
  return {
    total: list.length,
    withEmailCount: withEmail.length,
    withoutEmailCount: withoutEmail.length,
    withoutEmailNames: withoutEmail
      .slice(0, 5)
      .map((c) => c.nome || c.razaosocial || `#${c.id}`),
  };
}
